import React from 'react';
import { FileText } from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12 border-t border-gray-700" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Column 1: Brand */}
          <div className="col-span-2 md:col-span-1 space-y-4">
            <a href="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold">
                <span className="text-orange-400">Paperwork</span>
                <span className="text-white">Depot</span>
              </h3>
            </a>
            <p className="text-gray-400 text-sm leading-relaxed">
              Professional legal documentation guides for individuals and businesses. Clear, structured, and ready to use.
            </p>
          </div>

          {/* Column 2: Categories */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-orange-400 uppercase tracking-wider">Categories</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/category/credit-disputes" className="hover:text-white transition-colors">Credit Disputes</a></li>
              <li><a href="/category/business-credit" className="hover:text-white transition-colors">Business Credit</a></li>
              <li><a href="/category/court-motions" className="hover:text-white transition-colors">Court Motions</a></li>
              <li><a href="/category/affidavits" className="hover:text-white transition-colors">Affidavits</a></li>
              <li><a href="/category/trust-estate" className="hover:text-white transition-colors">Trust & Estate</a></li>
              <li><a href="/category/legal-notices" className="hover:text-white transition-colors">Legal Notices</a></li>
              <li><a href="/category/landlord-tenant" className="hover:text-white transition-colors">Landlord-Tenant</a></li>
              <li><a href="/category/lawsuits" className="hover:text-white transition-colors">Civil Lawsuit Templates</a></li>
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-orange-400 uppercase tracking-wider">Resources</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/browse" className="hover:text-white transition-colors">Browse All Guides</a></li>
              <li><a href="/bundles" className="hover:text-white transition-colors">Bundles & Deals</a></li>
              <li><a href="/free-guide" className="hover:text-white transition-colors">Free Legal Guide</a></li>
              <li><a href="/trending" className="hover:text-white transition-colors">Trending Templates</a></li>
              <li><a href="/build-bundle" className="hover:text-white transition-colors">Build Your Bundle</a></li>
            </ul>
          </div>

          {/* Column 4: Support */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-orange-400 uppercase tracking-wider">Support</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/support" className="hover:text-white transition-colors">Contact</a></li>
              <li><a href="/terms" className="hover:text-white transition-colors">Terms of Use</a></li>
              <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="/refund" className="hover:text-white transition-colors">Refund Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-700 flex flex-col items-center gap-4">
          <div className="flex items-center gap-5">
            <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-400 transition-colors" data-testid="social-instagram" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </a>
            <a href="https://www.pinterest.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-400 transition-colors" data-testid="social-pinterest" aria-label="Pinterest">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.993 3.995-.282 1.193.599 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.091.375-.293 1.199-.334 1.363-.053.225-.174.271-.402.163-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
            </a>
            <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" data-testid="social-tiktok" aria-label="TikTok">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 448 512" fill="currentColor"><path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/></svg>
            </a>
          </div>
          <p className="text-gray-500 text-xs">&copy; 2016 PaperworkDepot.com. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
